/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IntegrationAppProvider, useIntegrationApp } from "@membranehq/react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Mail,
  CalendarDays,
  Video,
  ShieldCheck,
  Sparkles,
  Building2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Switch } from "../../../components/ui/switch";
import { Textarea } from "../../../components/ui/textarea";
import { Badge } from "../../../components/ui/badge";
import DomainListInput from "../../../shared/forms/DomainListInput";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import useSelectWorkspace from "../../../hooks/useSelectWorkspace";
import { getProvider } from "../../../integrations/providers";
import { generateIntegrationAppToken } from "../../../services/integrationAppEndpoint";
import { triggerBestEffortInitialSync, notifyBackendOfConnection } from "../../../integrations/integrationAppPostConnect";
import { activateInboundSyncForProvider } from "../../../integrations/activateInboundSync";
import { getMemberIntegrationsMe } from "../../../services/memberIntegrationsEndpoint";
import { listAIMemoryObjects } from "../../../services/aiMemory/aiMemoryEndpoints";
import { getSyncConfigurations } from "../../../services/syncConfiguration/syncConfigurationEndpoints";
import { updateWorkspace } from "../../../services/workspace/workspaceEndpoints";
import {
  queryClient,
  useCreateSyncConfiguration,
  useEmailSettings,
  useUpdateEmailSettings,
  useEmailDomainReview,
  useMemberIntegrationsMe,
  useMeetingNoteTakerPreference,
  useUpdateSyncConfiguration,
  useUpsertMeetingNoteTakerPreference,
} from "../../../hooks/QueryHooks";
import useObjectSearchTable from "../../../shared/table/useObjectSearchTable";
import type { EmailSettings } from "../../../services/emailSettingsEndpoint";

const normalizeProvider = (provider: string | undefined) => (provider || "").toLowerCase().replace(/_/g, "-");

const STEPS = [
  { key: "intro", label: "Intro" },
  { key: "gmail", label: "Email" },
  { key: "configure", label: "Configure" },
  { key: "calendar", label: "Calendar" },
  { key: "recorder", label: "Recorder" },
  { key: "sync", label: "Sync" },
] as const;

type StepKey = (typeof STEPS)[number]["key"];

// ---- Member-level IntegrationApp provider (mirrors GmailPage / GoogleCalendarPage) ----
const MemberLevelIntegrationProvider = ({ children }: { children: React.ReactNode }) => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);

  const fetchMemberLevelToken = async () => {
    if (workspace?.id) {
      try {
        const response = await generateIntegrationAppToken({
          workspace_id: workspace.id,
          isMemberLevel: true,
        });
        return response?.data?.token;
      } catch (error) {
        console.error("Failed to fetch member-level integration token:", error);
        return null;
      }
    }
    return null;
  };

  return (
    <IntegrationAppProvider fetchToken={fetchMemberLevelToken}>
      {children}
    </IntegrationAppProvider>
  );
};

// ---- Stepper header ----
const Stepper = ({ activeIndex }: { activeIndex: number }) => (
  <div className="mb-6 flex items-center gap-1.5">
    {STEPS.map((step, index) => {
      const done = index < activeIndex;
      const current = index === activeIndex;
      return (
        <div key={step.key} className="flex flex-1 items-center gap-1.5">
          <span
            className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-medium"
            style={{
              backgroundColor: done || current ? "#8267E6" : "transparent",
              color: done || current ? "#FFFFFF" : "#B0B5C0",
              border: done || current ? "none" : "1px solid #D0D0D0",
            }}
          >
            {done ? <Check className="h-3 w-3" /> : index + 1}
          </span>
          {index < STEPS.length - 1 && (
            <span className="h-px flex-1" style={{ backgroundColor: done ? "#8267E6" : "#EEEEEE" }} />
          )}
        </div>
      );
    })}
  </div>
);

// ---- Connect button shared between Gmail + Calendar steps ----
type ConnectStepProps = {
  providerKey: "gmail" | "google-calendar";
  objectSlug: "emails" | "meetings";
  connected: boolean;
  workspaceId?: string;
  onConnected: (connectionId?: string) => void;
};

const ConnectButton = ({ providerKey, objectSlug, connected, workspaceId, onConnected }: ConnectStepProps) => {
  const provider = getProvider(providerKey);
  const integrationApp = useIntegrationApp();
  const [loading, setLoading] = useState(false);

  // Sync-configuration management used to fully activate the inbound sync on connect
  const createSyncConfigMutation = useCreateSyncConfiguration();
  const updateSyncConfigMutation = useUpdateSyncConfiguration();

  // AI-memory slug + source collection differ from the membrane object slug.
  const aiMemorySlug = providerKey === "gmail" ? "conversation_messages" : "meetings";

  // Fully activate the inbound sync — mirrors the manual "Start sync" recipe.
  // Falls back to a best-effort flow trigger if prerequisites can't be resolved.
  const activateInbound = async (selector: string) => {
    try {
      // Resolve the parent integration_id (the member integration's integration_id).
      let resolvedIntegrationId: string | undefined;
      let destinationObjectId: string | undefined;
      let existingSyncConfigs: any = [];

      if (workspaceId) {
        try {
          const resp: any = await getMemberIntegrationsMe({ workspace_id: workspaceId });
          const list = resp?.data || [];
          resolvedIntegrationId = list.find((mi: any) => normalizeProvider(mi?.provider) === providerKey)?.integration_id;
        } catch (e) {
          // best-effort
        }
        try {
          // The backend auto-enables the AI-memory objects on connect; fetch them
          // fresh to obtain the object_definition id before activating.
          const aiResp = await listAIMemoryObjects({ workspace_id: workspaceId });
          destinationObjectId = (aiResp?.data || []).find((o: any) => o?.api_slug === aiMemorySlug)?.object_id;
        } catch (e) {
          // best-effort
        }
        try {
          existingSyncConfigs = await getSyncConfigurations({ workspace_id: workspaceId });
        } catch (e) {
          // best-effort
        }
      }

      const activated = await activateInboundSyncForProvider({
        integrationApp,
        workspaceId,
        integrationId: resolvedIntegrationId,
        providerKey,
        slug: aiMemorySlug,
        destinationObjectId,
        sourceObject: (provider.defaultSourceCollections as any)?.[aiMemorySlug],
        createSyncConfig: (args) => createSyncConfigMutation.mutateAsync(args),
        updateSyncConfig: (args) => updateSyncConfigMutation.mutateAsync(args),
        existingSyncConfigs,
      });

      if (!activated) {
        // Could not resolve sync-config/object id — fall back to best-effort.
        await triggerBestEffortInitialSync(integrationApp, selector);
      }
    } catch (e) {
      await triggerBestEffortInitialSync(integrationApp, selector);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const connection: any = await integrationApp.integration(provider.key).connect();
      if (connection && connection.state === "READY") {
        const connectionId = connection?.id;
        const selector = connectionId || provider.key;
        // Best-effort: ensure data source + field mapping exist (mirrors Gmail/Calendar pages)
        try {
          await Promise.allSettled([
            integrationApp.connection(selector).dataSource(objectSlug).get({ autoCreate: true }),
          ]);
          await Promise.allSettled([
            integrationApp.connection(selector).fieldMapping(objectSlug).get({ autoCreate: true }),
          ]);
        } catch (e) {
          console.error("Failed to ensure data source/field mapping", provider.key, e);
        }

        // Canonical backend upsert — resolves the member from auth, auto-creates the
        // parent Integrations row + WorkspaceMemberIntegration row, and enables the
        // provider's AI-memory objects. Best-effort; must not block onConnected.
        if (workspaceId) {
          try {
            const getEmailResponse = await integrationApp.connection(selector).action("get-user-email").run();
            const email =
              (getEmailResponse as any)?.output?.data?.emailAddress ||
              (getEmailResponse as any)?.output?.data?.id ||
              `${providerKey}@${selector}`;
            await notifyBackendOfConnection(workspaceId, providerKey, selector, email, email);
          } catch (e) {
            console.warn("Failed to notify backend of connection", providerKey, e);
          }
        }

        // Fully activate the inbound sync now that the backend has enabled the objects.
        await activateInbound(selector);

        queryClient.invalidateQueries(["member-integrations"]);
        onConnected(connectionId);
      }
    } catch (error) {
      console.error("Connection failed:", error);
      toast.error(`Could not connect ${provider.label}.`);
    } finally {
      setLoading(false);
    }
  };

  if (connected) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-status-active/30 bg-status-active/5 px-3 py-1.5 text-[12px] font-medium text-status-active">
        <Check className="h-3.5 w-3.5" />
        Connected
      </span>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      className="h-8 text-[12px]"
      onClick={handleConnect}
      disabled={loading}
    >
      {loading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
      Connect {provider.label}
    </Button>
  );
};

// ---- The wizard (rendered inside the member-level provider) ----
const EmailSetupWizardInner = ({ workspaceId, slug }: { workspaceId?: string; slug?: string }) => {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const activeStep: StepKey = STEPS[stepIndex].key;

  // Member integrations -> connected state for gmail + calendar
  const { data: memberIntegrations, refetch: refetchMemberIntegrations }: any = useMemberIntegrationsMe({
    workspaceId: workspaceId as string,
    enabled: !!workspaceId,
  });

  const [gmailConnectedLocal, setGmailConnectedLocal] = useState(false);
  const [calendarConnectedLocal, setCalendarConnectedLocal] = useState(false);

  const gmailConnected = useMemo(
    () => gmailConnectedLocal
      || (memberIntegrations || []).some((mi: any) => normalizeProvider(mi?.provider) === "gmail"),
    [memberIntegrations, gmailConnectedLocal],
  );
  const calendarConnected = useMemo(
    () => calendarConnectedLocal
      || (memberIntegrations || []).some((mi: any) => normalizeProvider(mi?.provider) === "google-calendar"),
    [memberIntegrations, calendarConnectedLocal],
  );

  // Email settings (configure step)
  const updateEmailSettings = useUpdateEmailSettings();
  const emailSettingsQuery = useEmailSettings({ workspaceId });
  const settings = emailSettingsQuery.data as EmailSettings | undefined;

  const [internalDomains, setInternalDomains] = useState<string[]>([]);
  const [disallowList, setDisallowList] = useState<string[]>([]);
  const [classifierInstructions, setClassifierInstructions] = useState("");
  const [configHydrated, setConfigHydrated] = useState(false);

  useEffect(() => {
    if (!settings || configHydrated) return;
    setInternalDomains(settings.internal_domains ?? []);
    setDisallowList(settings.domain_disallow_list ?? []);
    setClassifierInstructions(settings.classifier_instructions ?? "");
    setConfigHydrated(true);
  }, [settings, configHydrated]);

  // Recorder (note-taker) preference
  const recorderPref: any = useMeetingNoteTakerPreference({ workspaceId: workspaceId as string });
  const upsertRecorder = useUpsertMeetingNoteTakerPreference();
  const recorderEnabled = Boolean(recorderPref?.data?.enabled);

  const enableRecorder = async () => {
    if (!workspaceId) return;
    try {
      await upsertRecorder.mutateAsync({
        workspace_id: workspaceId,
        workspace_member_id: null,
        data: {
          enabled: true,
          provider: "recall_ai",
          min_meeting_duration_minutes: 5,
          allowed_sources: ["google_calendar"],
        },
      });
      queryClient.invalidateQueries(["meeting-note-taker-preference", workspaceId]);
      toast.success("Meeting recorder enabled.");
    } catch {
      toast.error("Could not enable the recorder.");
    }
  };

  // Live sync counts (sync step) — poll via refetch interval
  const isSyncStep = activeStep === "sync";
  const accountsTable = useObjectSearchTable({
    workspaceId,
    entityType: "accounts",
    enabled: isSyncStep && !!workspaceId,
    initialPageSize: 1,
    queryOptions: {},
  });
  const usersTable = useObjectSearchTable({
    workspaceId,
    entityType: "users",
    enabled: isSyncStep && !!workspaceId,
    initialPageSize: 1,
    queryOptions: {},
  });
  const { data: pendingReview }: any = useEmailDomainReview({
    workspaceId,
    status: "pending",
    enabled: isSyncStep && !!workspaceId,
  } as any);

  const accountsRefetch = accountsTable.refetch;
  const usersRefetch = usersTable.refetch;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!isSyncStep || !workspaceId) return undefined;
    pollRef.current = setInterval(() => {
      accountsRefetch?.();
      usersRefetch?.();
      queryClient.invalidateQueries(["email-domain-review", workspaceId]);
    }, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isSyncStep, workspaceId, accountsRefetch, usersRefetch]);

  // ---- Step navigation ----
  const goNext = () => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1));
  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));
  // From the intro step, return to the data-source picker. Use an explicit route
  // (not navigate(-1)) so it still works after a refresh or a deep link.
  const exitToIntegrations = () => {
    navigate(slug ? `/onboarding/workspace/${slug}/integrations` : "../integrations");
  };

  const handleGmailConnected = () => {
    setGmailConnectedLocal(true);
    if (workspaceId) {
      // Flip the backend email-first KV flag on successful connect.
      updateEmailSettings.mutate({ workspace_id: workspaceId, create_from_email: true });
    }
    refetchMemberIntegrations?.();
  };

  const saveConfigAndContinue = async () => {
    if (!workspaceId) {
      goNext();
      return;
    }
    try {
      await updateEmailSettings.mutateAsync({
        workspace_id: workspaceId,
        internal_domains: internalDomains,
        domain_disallow_list: disallowList,
        classifier_instructions: classifierInstructions.trim() ? classifierInstructions : null,
      });
      goNext();
    } catch {
      // toast handled by mutation; do not advance on failure
    }
  };

  const finish = async () => {
    // Persist setup_complete so the workspace is marked onboarded. This mirrors
    // the Segment/Native flows: the backend PUT updates BOTH the DB column and
    // PropelAuth org_metadata, and RootPage redirects off the org_metadata value.
    // Without this, a reload reads setup_complete=false and bounces the user
    // back to onboarding.
    if (workspaceId) {
      try {
        await updateWorkspace({ workspace_id: workspaceId, setup_complete: true });
        // RootPage reads setup_complete from the DB-backed workspace query, so
        // refresh that cache (24h staleTime) to avoid a stale "incomplete" read
        // bouncing the user back to onboarding within this session.
        queryClient.invalidateQueries(["workspace"]);
      } catch {
        // Non-fatal: still let the user into the workspace; they can retry later.
      }
    }
    if (slug) {
      navigate(`/home/${slug}/headquarters`, { replace: true, state: { setupComplete: true } });
    } else {
      navigate("/workspaces");
    }
  };

  const accountsCount = accountsTable.total || 0;
  const contactsCount = usersTable.total || 0;
  const pendingCount = pendingReview?.pending_count ?? pendingReview?.total ?? 0;

  // ---- Footer (Back / Continue) ----
  const renderFooter = () => {
    if (activeStep === "intro") {
      return (
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" className="h-8 text-[12px]" onClick={exitToIntegrations}>
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Back
          </Button>
          <Button size="sm" className="h-8 text-[12px]" onClick={goNext}>
            Get started
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      );
    }

    if (activeStep === "sync") {
      return (
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" className="h-8 text-[12px]" onClick={goBack}>
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Back
          </Button>
          <Button size="sm" className="h-8 text-[12px]" onClick={finish}>
            Go to your workspace
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      );
    }

    const optional = activeStep === "calendar" || activeStep === "recorder";
    const canContinue = activeStep === "gmail" ? gmailConnected : true;

    const onContinue = () => {
      if (activeStep === "configure") {
        saveConfigAndContinue();
        return;
      }
      goNext();
    };

    return (
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" className="h-8 text-[12px]" onClick={goBack}>
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          {optional && (
            <Button variant="ghost" size="sm" className="h-8 text-[12px] text-muted-foreground" onClick={goNext}>
              Skip
            </Button>
          )}
          <Button
            size="sm"
            className="h-8 text-[12px]"
            onClick={onContinue}
            disabled={!canContinue || (activeStep === "configure" && updateEmailSettings.isLoading)}
          >
            {activeStep === "configure" && updateEmailSettings.isLoading && (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            )}
            Continue
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <section
      className="flex w-full max-w-[600px] flex-col rounded-lg border border-[#EEEEEE] bg-white p-10"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
    >
      <Stepper activeIndex={stepIndex} />

      {/* ---------- Step 1: Intro ---------- */}
      {activeStep === "intro" && (
        <div className="space-y-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg" style={{ backgroundColor: "#8267E6" }}>
            <Mail className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-[15px] font-semibold" style={{ color: "#181818" }}>
              Build your book of business from your inbox
            </h2>
            <p className="mt-1.5 text-[13px]" style={{ color: "#5C6070" }}>
              We&apos;ll build your accounts &amp; contacts automatically from your email — no code, no product
              data required. Connect your inbox, set a few preferences, and Hyperengage does the rest.
            </p>
          </div>
          <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 px-4 py-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <div>
              <p className="text-[12px] font-medium text-foreground">Your privacy is protected</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                We skip HR, legal, investor and personal threads, free-mail providers, and your own internal
                domains. Sensitive threads are dropped before anything is created.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Step 2: Connect Gmail (required) ---------- */}
      {activeStep === "gmail" && (
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-semibold" style={{ color: "#181818" }}>Connect your email</h2>
              <Badge variant="secondary" className="text-[10px]">Required</Badge>
            </div>
            <p className="mt-1.5 text-[13px]" style={{ color: "#5C6070" }}>
              Connect Gmail so Hyperengage can detect the companies and people you talk to and build your
              accounts &amp; contacts automatically.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                <Mail className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <p className="text-[13px] font-medium text-foreground">Gmail</p>
                <p className="text-[11px] text-muted-foreground">
                  {gmailConnected ? "Your inbox is connected." : "We only read message metadata to build accounts."}
                </p>
              </div>
            </div>
            <ConnectButton
              providerKey="gmail"
              objectSlug="emails"
              connected={gmailConnected}
              workspaceId={workspaceId}
              onConnected={handleGmailConnected}
            />
          </div>
          {!gmailConnected && (
            <p className="text-[11px] text-muted-foreground">Connect Gmail to continue.</p>
          )}
        </div>
      )}

      {/* ---------- Step 3: Configure (required) ---------- */}
      {activeStep === "configure" && (
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-semibold" style={{ color: "#181818" }}>Configure detection</h2>
              <Badge variant="secondary" className="text-[10px]">Required</Badge>
            </div>
            <p className="mt-1.5 text-[13px]" style={{ color: "#5C6070" }}>
              Tell us which domains are yours and which to ignore. You can change all of this later in Settings.
            </p>
          </div>

          <DomainListInput
            label="Internal domains"
            description="Your own company domains. Email between internal addresses is never used to create accounts."
            placeholder="acme.com"
            values={internalDomains}
            onChange={setInternalDomains}
            disabled={updateEmailSettings.isLoading}
          />

          <div className="space-y-2">
            <p className="text-[13px] font-medium text-foreground">Classifier instructions</p>
            <p className="text-[12px] text-muted-foreground">
              Optional guidance for the AI classifier on which domains count as customers.
            </p>
            <Textarea
              value={classifierInstructions}
              onChange={(event) => setClassifierInstructions(event.target.value)}
              disabled={updateEmailSettings.isLoading}
              placeholder="e.g. Treat domains of paying customers as accounts. Ignore vendors, recruiters, and personal email providers."
              className="min-h-[90px] text-[13px]"
            />
          </div>

          <DomainListInput
            label="Disallow list"
            description="Domains that should never create accounts (e.g. vendors, personal email providers)."
            placeholder="gmail.com"
            values={disallowList}
            onChange={setDisallowList}
            disabled={updateEmailSettings.isLoading}
          />
        </div>
      )}

      {/* ---------- Step 4: Connect calendar (optional) ---------- */}
      {activeStep === "calendar" && (
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-semibold" style={{ color: "#181818" }}>Connect your calendar</h2>
              <Badge variant="outline" className="text-[10px]">Optional</Badge>
            </div>
            <p className="mt-1.5 text-[13px]" style={{ color: "#5C6070" }}>
              Add Google Calendar so meetings with your accounts show up automatically. You can skip this and
              add it later.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                <CalendarDays className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <p className="text-[13px] font-medium text-foreground">Google Calendar</p>
                <p className="text-[11px] text-muted-foreground">
                  {calendarConnected ? "Your calendar is connected." : "Sync meetings to your accounts and contacts."}
                </p>
              </div>
            </div>
            <ConnectButton
              providerKey="google-calendar"
              objectSlug="meetings"
              connected={calendarConnected}
              workspaceId={workspaceId}
              onConnected={() => {
                setCalendarConnectedLocal(true);
                refetchMemberIntegrations?.();
              }}
            />
          </div>
        </div>
      )}

      {/* ---------- Step 5: Enable recorder (optional) ---------- */}
      {activeStep === "recorder" && (
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-semibold" style={{ color: "#181818" }}>Enable the meeting recorder</h2>
              <Badge variant="outline" className="text-[10px]">Optional</Badge>
            </div>
            <p className="mt-1.5 text-[13px]" style={{ color: "#5C6070" }}>
              Let Hyperengage auto-join and transcribe your external meetings, so call notes land on
              the right accounts automatically.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                <Video className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <p className="text-[13px] font-medium text-foreground">Auto-record external meetings</p>
                <p className="text-[11px] text-muted-foreground">
                  Records meetings with external participants (5+ minutes).
                </p>
              </div>
            </div>
            <Switch
              checked={recorderEnabled}
              disabled={upsertRecorder.isLoading}
              onCheckedChange={(checked) => {
                if (checked) enableRecorder();
              }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            You can fine-tune recording rules anytime in Settings → Call Intelligence.
          </p>
        </div>
      )}

      {/* ---------- Step 6: Sync progress ---------- */}
      {activeStep === "sync" && (
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" style={{ color: "#8267E6" }} />
            <h2 className="text-[15px] font-semibold" style={{ color: "#181818" }}>
              Building your book of business…
            </h2>
          </div>
          <p className="text-[13px]" style={{ color: "#5C6070" }}>
            We&apos;re scanning your inbox and building accounts and contacts. This keeps running in the
            background — you can head into your workspace now.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building2 className="h-4 w-4" />
                <span className="text-[11px]">Accounts detected</span>
              </div>
              <p className="mt-1 text-[22px] font-semibold text-foreground">
                {accountsTable.isFetching && !accountsCount ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  accountsCount
                )}
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                <span className="text-[11px]">Contacts detected</span>
              </div>
              <p className="mt-1 text-[22px] font-semibold text-foreground">
                {usersTable.isFetching && !contactsCount ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  contactsCount
                )}
              </p>
            </div>
          </div>

          {pendingCount > 0 && (
            <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 px-4 py-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <div>
                <p className="text-[12px] font-medium text-foreground">
                  {pendingCount} {pendingCount === 1 ? "domain" : "domains"} to review
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Some domains need your confirmation before we create accounts. Review them anytime in
                  Settings → Email.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 border-t border-[#EEEEEE] pt-5">{renderFooter()}</div>
    </section>
  );
};

const EmailSetupWizard = () => {
  const { data }: any = useSelectWorkspace();
  const workspaceId = data?.id as string | undefined;
  const slug = data?.unique_identifier as string | undefined;

  return (
    <MemberLevelIntegrationProvider>
      <EmailSetupWizardInner workspaceId={workspaceId} slug={slug} />
    </MemberLevelIntegrationProvider>
  );
};

export default EmailSetupWizard;
